import QRCodeStyling, {
  CornerDotType,
  CornerSquareType,
  DotType,
  DrawType,
  ErrorCorrectionLevel,
  Mode,
  TypeNumber,
} from 'qr-code-styling';

const initialize = {
  type: 'svg' as DrawType,
  margin: 2,
  qrOptions: {
    typeNumber: 0 as TypeNumber,
    mode: 'Byte' as Mode,
    errorCorrectionLevel: 'H' as ErrorCorrectionLevel,
  },
  imageOptions: {
    hideBackgroundDots: true,
    imageSize: 0.5,
    margin: 5,
    crossOrigin: 'anonymous',
  },
};

export type QrShape = 'rounded' | 'square';

/** What every QR surface in the product has always drawn. */
const ROUNDED_SHAPE = {
  dotsOptions: {
    size: 10,
    type: 'extra-rounded' as DotType,
  },
  cornersSquareOptions: {
    type: 'extra-rounded' as CornerSquareType,
  },
};

/**
 * The campaign fallback artboard, which specifies square modules and square finder patterns.
 *
 * The corner styles are stated rather than left to derive from dotsOptions so that changing
 * the module shape cannot silently reshape the finder patterns with it.
 */
const SQUARE_SHAPE = {
  dotsOptions: {
    size: 10,
    type: 'square' as DotType,
  },
  cornersSquareOptions: {
    type: 'square' as CornerSquareType,
  },
  cornersDotOptions: {
    type: 'square' as CornerDotType,
  },
};

const shapeOptions = (shape: QrShape = 'rounded') =>
  shape === 'square' ? SQUARE_SHAPE : ROUNDED_SHAPE;

/** Full-canvas rects — the background and the clip paths — rather than modules of the code. */
const isBackdropRect = (width: number, canvasSize: number) => width > canvasSize * 0.9;

const RECT_PATTERN = /<rect[^>]*?x="([\d.-]+)"[^>]*?y="([\d.-]+)"[^>]*?width="([\d.]+)"[^>]*?height="([\d.]+)"/g;

/**
 * Pulls the viewBox in to the modules themselves.
 *
 * The library lays the code out on a fixed 300-unit canvas and sizes each module to a whole
 * number of units, so whatever does not divide evenly is left as slack around the edge —
 * for a typical URL, 20 units a side. That slack is invisible on its own, but it is white
 * space inside the image, so it adds to whatever padding the caller puts around it and the
 * two silently stack.
 *
 * Trimming it here rather than compensating at the call site means the caller's padding is
 * the whole quiet zone and is worth exactly what it says, at any URL length — the slack
 * changes with the module count, so a fixed offset would only be right for one of them.
 */
const trimSvgQuietZone = (svgString: string): string => {
  const canvas = svgString.match(/viewBox="0 0 ([\d.]+) [\d.]+"/);
  if (!canvas) return svgString;

  const canvasSize = Number(canvas[1]);
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;

  for (const [, x, y, width, height] of svgString.matchAll(RECT_PATTERN)) {
    if (isBackdropRect(Number(width), canvasSize)) continue;
    min = Math.min(min, Number(x), Number(y));
    max = Math.max(max, Number(x) + Number(width), Number(y) + Number(height));
  }

  // No modules matched: a markup change we have not seen. The untrimmed code still scans.
  if (!Number.isFinite(min) || max <= min) return svgString;

  return svgString.replace(/viewBox="[^"]*"/, `viewBox="${min} ${min} ${max - min} ${max - min}"`);
};

export const generateQRCode = async (
  value: string,
  options?: {
    logo?: string;
    /** Defaults to the library's white. Set this to sit the code on a tinted card. */
    backgroundColor?: string;
    /**
     * Drops the white margin the library leaves around the code, so the modules fill the
     * element. Set this when the code sits on a padded card that is already providing the
     * quiet zone, and leave it off anywhere the code might land directly on artwork.
     */
    trimQuietZone?: boolean;
    /**
     * Module and finder-pattern shape. Defaults to the rounded styling every other QR
     * surface draws; `square` is the campaign fallback artboard's own and is opt-in so that
     * one screen's design decision cannot restyle codes it has nothing to do with.
     */
    shape?: QrShape;
  },
) => {
  const x = {
    ...initialize,
    ...shapeOptions(options?.shape),
    image: options?.logo || undefined,
    data: value,
    ...(options?.backgroundColor ? { backgroundOptions: { color: options.backgroundColor } } : {}),
  };
  const svg = await new QRCodeStyling(x).getRawData('svg');
  if (!svg) return null;

  let svgString: string;
  if (svg instanceof Blob) {
    svgString = await svg.text();
  } else {
    svgString = svg.toString();
  }

  const base64 = btoa(options?.trimQuietZone ? trimSvgQuietZone(svgString) : svgString);
  return `data:image/svg+xml;base64,${base64}`;
};

export const generateQRCodePng = async (value: string, options?: { logo?: string }) => {
  const x = {
    ...initialize,
    ...shapeOptions(),
    image: options?.logo || undefined,
    data: value,
  };
  const png = await new QRCodeStyling(x).getRawData('png');
  if (!png) return null;

  let arrayBuffer: ArrayBuffer;
  if (png instanceof Blob) {
    arrayBuffer = await png.arrayBuffer();
  } else {
    arrayBuffer = (png as Buffer).buffer.slice(png.byteOffset, png.byteOffset + png.byteLength) as ArrayBuffer;
  }

  // Convert ArrayBuffer to base64
  const uint8Array = new Uint8Array(arrayBuffer);
  const base64 = btoa(String.fromCharCode.apply(null, Array.from(uint8Array)));
  return `data:image/png;base64,${base64}`;
};
